// src/hooks/useConversationContext.js - Multi-conversation support

import { useState, useEffect, useCallback, useRef } from 'react';
import { db } from '../firebase';
import {
  doc, getDoc, setDoc, updateDoc, deleteDoc, arrayUnion,
  collection, getDocs, addDoc, query, orderBy
} from 'firebase/firestore';
import api, { streamPost } from '../api';


// Charts are stored with the message so a conversation reopens with its
// figures. A five-year series is thousands of points; keep enough to draw.
function trimChart(config) {
  if (!config || !Array.isArray(config.series)) return config || null;
  const keep = 130;
  const pick = (arr) => {
    if (!arr || arr.length <= keep) return arr;
    const step = (arr.length - 1) / (keep - 1);
    return Array.from({ length: keep }, (_, i) => arr[Math.round(i * step)]);
  };
  return {
    ...config,
    series: pick(config.series).map(({ date, open, high, low, close }) => ({ date, open, high, low, close })),
    benchmark: config.benchmark ? { ...config.benchmark, closes: pick(config.benchmark.closes) } : null,
  };
}

const useConversationContext = (user) => {
  const [conversations, setConversations] = useState([]);
  const [activeConversationId, setActiveConversationId] = useState(null);
  const [conversationHistory, setConversationHistory] = useState([]);
  const [userProfile, setUserProfile] = useState(null);
  const [loading, setLoading] = useState(true);
  const [usageInfo, setUsageInfo] = useState(null);
  const [isMessageLoading, setIsMessageLoading] = useState(false);
  const [messageError, setMessageError] = useState(null);

  // Track whether the active conversation has been titled yet
  const hasAutoTitled = useRef(false);

  // ── Firestore helpers ──

  const conversationsRef = useCallback(() => {
    if (!user?.uid) return null;
    return collection(db, 'users', user.uid, 'conversations');
  }, [user?.uid]);

  const conversationDocRef = useCallback((convId) => {
    if (!user?.uid || !convId) return null;
    return doc(db, 'users', user.uid, 'conversations', convId);
  }, [user?.uid]);

  // ── Load conversation list (metadata only) ──

  const loadConversations = useCallback(async () => {
    const colRef = conversationsRef();
    if (!colRef) return [];

    try {
      const q = query(colRef, orderBy('updatedAt', 'desc'));
      const snapshot = await getDocs(q);
      const convos = snapshot.docs.map(d => ({
        id: d.id,
        title: d.data().title || 'New Chat',
        createdAt: d.data().createdAt?.toDate?.() || d.data().createdAt || new Date(),
        updatedAt: d.data().updatedAt?.toDate?.() || d.data().updatedAt || new Date(),
      }));
      setConversations(convos);
      return convos;
    } catch (error) {
      console.error('Error loading conversations:', error);
      return [];
    }
  }, [conversationsRef]);

  // ── Load messages for a specific conversation ──

  const loadConversationMessages = useCallback(async (convId) => {
    const docRef = conversationDocRef(convId);
    if (!docRef) return;

    try {
      const snap = await getDoc(docRef);
      if (snap.exists()) {
        const data = snap.data();
        setConversationHistory(data.messages || []);
        hasAutoTitled.current = !!(data.title && data.title !== 'New Chat');
      } else {
        setConversationHistory([]);
        hasAutoTitled.current = false;
      }
    } catch (error) {
      console.error('Error loading conversation messages:', error);
      setConversationHistory([]);
    }
  }, [conversationDocRef]);

  // ── Create a new conversation ──

  const createConversation = useCallback(async () => {
    const colRef = conversationsRef();
    if (!colRef) return null;

    try {
      const now = new Date();
      const newDoc = await addDoc(colRef, {
        title: 'New Chat',
        createdAt: now,
        updatedAt: now,
        messages: []
      });

      const newConvo = { id: newDoc.id, title: 'New Chat', createdAt: now, updatedAt: now };
      setConversations(prev => [newConvo, ...prev]);
      setActiveConversationId(newDoc.id);
      setConversationHistory([]);
      hasAutoTitled.current = false;
      return newDoc.id;
    } catch (error) {
      console.error('Error creating conversation:', error);
      return null;
    }
  }, [conversationsRef]);

  // ── Switch to a conversation ──

  const switchConversation = useCallback(async (convId) => {
    if (convId === activeConversationId) return;
    setActiveConversationId(convId);
    await loadConversationMessages(convId);
  }, [activeConversationId, loadConversationMessages]);

  // ── Delete a conversation ──

  const deleteConversation = useCallback(async (convId) => {
    const docRef = conversationDocRef(convId);
    if (!docRef) return;

    try {
      await deleteDoc(docRef);
      setConversations(prev => prev.filter(c => c.id !== convId));

      // If we deleted the active conversation, switch to next or create new
      if (convId === activeConversationId) {
        const remaining = conversations.filter(c => c.id !== convId);
        if (remaining.length > 0) {
          await switchConversation(remaining[0].id);
        } else {
          await createConversation();
        }
      }
    } catch (error) {
      console.error('Error deleting conversation:', error);
    }
  }, [conversationDocRef, activeConversationId, conversations, switchConversation, createConversation]);

  // ── Rename a conversation ──

  const renameConversation = useCallback(async (convId, newTitle) => {
    const docRef = conversationDocRef(convId);
    if (!docRef) return;

    try {
      await updateDoc(docRef, { title: newTitle });
      setConversations(prev =>
        prev.map(c => c.id === convId ? { ...c, title: newTitle } : c)
      );
    } catch (error) {
      console.error('Error renaming conversation:', error);
    }
  }, [conversationDocRef]);

  // ── Add message to active conversation ──

  // Firestore caps a document at 1MB. Once a conversation gets long, rewrite
  // the array trimmed to the newest messages instead of appending forever.
  const MAX_STORED_MESSAGES = 200;

  const addMessagesToHistory = useCallback(async (newMessages, fullHistory) => {
    if (!user?.uid || !activeConversationId) return;

    try {
      const docRef = conversationDocRef(activeConversationId);
      if (!docRef) return;
      const payload = fullHistory.length > MAX_STORED_MESSAGES
        ? { messages: fullHistory.slice(-MAX_STORED_MESSAGES), updatedAt: new Date() }
        : { messages: arrayUnion(...newMessages), updatedAt: new Date() };
      await updateDoc(docRef, payload);

      // Update conversation list order (move to top)
      setConversations(prev => {
        const updated = prev.map(c =>
          c.id === activeConversationId ? { ...c, updatedAt: new Date() } : c
        );
        updated.sort((a, b) => new Date(b.updatedAt) - new Date(a.updatedAt));
        return updated;
      });
    } catch (error) {
      console.error('Error saving message to history:', error);
    }
  }, [user?.uid, activeConversationId, conversationDocRef]);

  // ── Get recent context for API ──

  const getRecentContext = useCallback((limit = 10) => {
    if (!conversationHistory || !Array.isArray(conversationHistory)) {
      return [];
    }
    return conversationHistory.slice(-limit).map(msg => ({
      role: msg.role,
      content: msg.content
    }));
  }, [conversationHistory]);

  // ── Migrate old conversationHistory from user doc ──

  const migrateOldHistory = useCallback(async (userData) => {
    const oldHistory = userData.conversationHistory;
    if (!oldHistory || oldHistory.length === 0) return false;

    const colRef = conversationsRef();
    if (!colRef) return false;

    try {
      const now = new Date();
      // Create a conversation from old messages
      const firstMsg = oldHistory.find(m => m.role === 'user');
      const title = firstMsg
        ? firstMsg.content.substring(0, 50) + (firstMsg.content.length > 50 ? '...' : '')
        : 'Previous Conversation';

      await addDoc(colRef, {
        title,
        createdAt: oldHistory[0]?.timestamp || now,
        updatedAt: now,
        messages: oldHistory
      });

      // Clear old field
      const userDocRef = doc(db, 'users', user.uid);
      await updateDoc(userDocRef, { conversationHistory: [] });

      return true;
    } catch (error) {
      console.error('Error migrating old history:', error);
      return false;
    }
  }, [conversationsRef, user?.uid]);

  // ── Load user data + conversations on mount ──

  const loadUserData = useCallback(async () => {
    if (!user?.uid) return;

    try {
      const userDocRef = doc(db, 'users', user.uid);
      const userDoc = await getDoc(userDocRef);

      if (userDoc.exists()) {
        const userData = userDoc.data();
        setUserProfile(userData.profile || null);

        // Migrate old history if present
        if (userData.conversationHistory && userData.conversationHistory.length > 0) {
          await migrateOldHistory(userData);
        }
      } else {
        // New user
        await setDoc(userDocRef, {
          email: user.email,
          displayName: user.displayName,
          createdAt: new Date(),
          conversationHistory: [],
          profile: null
        });
        setUserProfile(null);
      }

      // Load conversations
      const convos = await loadConversations();

      if (convos.length > 0) {
        // Open the most recent conversation
        setActiveConversationId(convos[0].id);
        await loadConversationMessages(convos[0].id);
      } else {
        // Create first conversation
        await createConversation();
      }
    } catch (error) {
      console.error('Error loading user data:', error);
      setConversationHistory([]);
      setUserProfile(null);
    } finally {
      setLoading(false);
    }
  }, [user, migrateOldHistory, loadConversations, loadConversationMessages, createConversation]);

  // Load on mount
  useEffect(() => {
    if (user?.uid) {
      loadUserData();
    } else {
      setLoading(false);
      setConversationHistory([]);
      setConversations([]);
      setActiveConversationId(null);
      setUserProfile(null);
    }
  }, [user, loadUserData]);

  // ── Send message ──

  const sendMessage = useCallback(async (message) => {
    setIsMessageLoading(true);
    setMessageError(null);

    try {
      const response = await api.post('/v1/chat', {
        message,
        context: getRecentContext(),
        user_profile: {
          ...userProfile,
          isPremium: user?.isPremium || false
        },
        preferences: userProfile?.preferences || {},
        user_id: user?.uid || null
      });

      if (response.status === 429) {
        const errorData = response.data.detail;
        setMessageError(errorData.message || 'Daily limit reached');
        return;
      }

      const data = response.data;

      if (data.usage) {
        setUsageInfo(data.usage);
      }

      const userMessage = { role: 'user', content: message, timestamp: new Date() };
      const assistantMessage = {
        role: 'assistant',
        content: data.response,
        timestamp: new Date(),
        chartConfig: trimChart(data.chart_config),
        chartSuggestions: data.chart_suggestions || null
      };

      const newHistory = [...(conversationHistory || []), userMessage, assistantMessage];
      setConversationHistory(newHistory);

      if (user?.uid) {
        await addMessagesToHistory([userMessage, assistantMessage], newHistory);
      }

      // Auto-title: set title from first user message
      if (!hasAutoTitled.current && activeConversationId) {
        const title = message.substring(0, 50) + (message.length > 50 ? '...' : '');
        await renameConversation(activeConversationId, title);
        hasAutoTitled.current = true;
      }

      return data;

    } catch (error) {
      console.error('Error sending message:', error);

      if (error.response?.status === 429) {
        const errorData = error.response.data.detail;
        setMessageError(errorData.message || 'Daily limit reached');
      } else {
        setMessageError(error.message || 'Failed to send message');
      }

      throw error;
    } finally {
      setIsMessageLoading(false);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.uid, user?.isPremium, userProfile, conversationHistory, getRecentContext, addMessagesToHistory, activeConversationId, renameConversation]);

  // ── Send message, streamed ──
  // Same contract as sendMessage; onDelta receives text as it arrives.

  const sendMessageStream = useCallback(async (message, onDelta) => {
    setIsMessageLoading(true);
    setMessageError(null);

    try {
      const data = await streamPost('/v1/chat/stream', {
        message,
        context: getRecentContext(),
        user_profile: {
          ...userProfile,
          isPremium: user?.isPremium || false
        },
        preferences: userProfile?.preferences || {},
        user_id: user?.uid || null
      }, {
        onEvent: (evt) => {
          if (evt.type === 'delta' && onDelta) onDelta(evt.text || '');
          if (evt.type === 'error') throw new Error(evt.message || 'The analyst could not answer.');
        }
      });

      if (data.usage) {
        setUsageInfo(data.usage);
      }

      const userMessage = { role: 'user', content: message, timestamp: new Date() };
      const assistantMessage = {
        role: 'assistant',
        content: data.response,
        timestamp: new Date(),
        chartConfig: trimChart(data.chart_config),
        chartSuggestions: data.chart_suggestions || null
      };

      const newHistory = [...(conversationHistory || []), userMessage, assistantMessage];
      setConversationHistory(newHistory);

      if (user?.uid) {
        await addMessagesToHistory([userMessage, assistantMessage], newHistory);
      }

      if (!hasAutoTitled.current && activeConversationId) {
        const title = message.substring(0, 50) + (message.length > 50 ? '...' : '');
        await renameConversation(activeConversationId, title);
        hasAutoTitled.current = true;
      }

      return data;
    } catch (error) {
      console.error('Error streaming message:', error);
      const detail = error.response?.data?.detail;
      if (error.response?.status === 429) {
        setMessageError((detail && detail.message) || 'Daily limit reached');
      } else {
        setMessageError((detail && detail.message) || error.message || 'Failed to send message');
      }
      throw error;
    } finally {
      setIsMessageLoading(false);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.uid, user?.isPremium, userProfile, conversationHistory, getRecentContext, addMessagesToHistory, activeConversationId, renameConversation]);

  // ── Update profile ──

  const updateUserProfile = useCallback(async (profileData) => {
    if (!user?.uid) return;

    setUserProfile(profileData);

    try {
      const userDocRef = doc(db, 'users', user.uid);
      await setDoc(userDocRef, {
        profile: profileData,
        updatedAt: new Date()
      }, { merge: true });
    } catch (error) {
      console.error('Error updating user profile:', error);
      throw error;
    }
  }, [user?.uid]);

  // ── Usage check ──

  const checkUsage = useCallback(async () => {
    if (!user?.uid) return;

    try {
      const response = await api.get(`/v1/portfolio/usage/check/${user.uid}`);
      setUsageInfo(response.data.usage);
    } catch (error) {
      if (error.response?.status === 404) {
        try {
          const fallbackResponse = await api.get(`/usage/check/${user.uid}`);
          setUsageInfo(fallbackResponse.data.usage);
        } catch (fallbackError) {
          console.error('Error checking usage (both endpoints failed):', fallbackError);
        }
      } else {
        console.error('Error checking usage:', error);
      }
    }
  }, [user?.uid]);

  useEffect(() => {
    if (user?.uid) {
      checkUsage();
    }
  }, [user?.uid, checkUsage]);

  const clearMessageError = useCallback(() => {
    setMessageError(null);
  }, []);

  return {
    conversationHistory: conversationHistory || [],
    userProfile,
    loading,
    usageInfo,
    isMessageLoading,
    messageError,
    addMessagesToHistory,
    updateUserProfile,
    clearConversationHistory: () => deleteConversation(activeConversationId),
    getRecentContext,
    sendMessage,
    sendMessageStream,
    checkUsage,
    clearMessageError,

    // Multi-conversation API
    conversations,
    activeConversationId,
    createConversation,
    switchConversation,
    deleteConversation,
    renameConversation,
  };
};

export default useConversationContext;
